import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { listProductSubscriptions, isUsable } from '@/api/products';
import { hasAuthToken } from '@/api/client';
import { PRODUCT_META, type ProductMeta, type TildeProduct } from '@/components/primitives/products';
import { useAuth } from './useAuth';
import { commercialProductFromTechnical, type CommercialProductCode } from '@/lib/commercialProducts';

/**
 * Producto activo del switcher multi-producto (eje "Producto"). Una organización híbrida puede
 * tener líneas técnicas Empresa/Estudio/API contratadas; el usuario se mueve entre experiencias
 * etiquetadas comercialmente.
 * `Admin` (consola interna) se ofrece sólo a SuperAdmin y no sale de las suscripciones.
 *
 * La selección se persiste por tenant en localStorage. Si el producto persistido ya no está
 * disponible (cambió la contratación), cae al primero disponible.
 */

// Fallback visual mientras se carga la suscripción. El alta real resuelve server-side Mono,
// Estudio/Base o API/Sandbox según la opción comercial elegida.
const DEFAULT_PRODUCT: TildeProduct = 'Empresa';
const MAX_TIMER_DELAY_MS = 2_147_000_000;

export interface ProductContextValue {
  activeProduct: TildeProduct;
  setActiveProduct: (product: TildeProduct) => void;
  /** Productos seleccionables: contratados (no cancelados) + Admin si es SuperAdmin. */
  availableProducts: TildeProduct[];
  /** Etiquetas comerciales para mostrar productos técnicos con nombres comprensibles por plan/tier. */
  productMeta: Record<TildeProduct, ProductMeta>;
  /** Código comercial canónico del producto activo; no depende de la copy visible. */
  activeCommercialProduct: CommercialProductCode | 'Admin';
  /** La suscripción activa tiene una identidad comercial canónica y operable. */
  hasConfirmedProduct: boolean;
  /** Tier técnico de la suscripción activa; null mientras el producto no esté confirmado. */
  activeTier: string | null;
  /** Features efectivas del tier activo. Se usan sólo para anticipar el gate del backend. */
  activeFeatures: readonly string[];
  /** Límites efectivos del tier activo. */
  activeLimits: Readonly<Record<string, number>>;
  isLoading: boolean;
}

export const ProductContext = createContext<ProductContextValue | null>(null);

function storageKey(tenantId: string): string {
  return `tilde-active-product-${tenantId}`;
}

export function ProductProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user, isAuthenticated } = useAuth();
  const tenantId = user?.tenantId ?? null;
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const [subscriptionNow, setSubscriptionNow] = useState(() => Date.now());

  const subsQ = useQuery({
    queryKey: ['product-subscriptions', tenantId],
    queryFn: listProductSubscriptions,
    enabled: Boolean(isAuthenticated && tenantId && hasAuthToken()),
    staleTime: 5 * 60_000,
  });

  // Reevalúa la disponibilidad al vencer el próximo trial, incluso si React Query conserva el
  // response en cache y el usuario deja abierta la pestaña.
  useEffect(() => {
    const nextTrialEnd = (subsQ.data ?? [])
      .filter((subscription) => subscription.status === 'Trial')
      .map((subscription) => Date.parse(subscription.nextBillingAt))
      .filter((timestamp) => Number.isFinite(timestamp) && timestamp > subscriptionNow)
      .sort((a, b) => a - b)[0];
    if (nextTrialEnd === undefined) return undefined;

    const delay = Math.min(nextTrialEnd - subscriptionNow + 25, MAX_TIMER_DELAY_MS);
    const timer = window.setTimeout(() => setSubscriptionNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [subsQ.data, subscriptionNow]);

  const availableProducts = useMemo<TildeProduct[]>(() => {
    const contracted = (subsQ.data ?? [])
      .filter((subscription) => isUsable(subscription, subscriptionNow))
      .map((s) => s.product);
    // Orden estable preferido; sólo los contratados. Admin se agrega aparte para SuperAdmin.
    const order: TildeProduct[] = ['Estudio', 'Empresa', 'Api'];
    const list = order.filter((p) => contracted.includes(p));
    // Fallback: si el backend aún no devolvió nada, asumimos la línea técnica Empresa,
    // con identidad pendiente hasta contar con una suscripción verificable.
    const base = list.length > 0 ? list : [DEFAULT_PRODUCT];
    return isSuperAdmin ? [...base, 'Admin'] : base;
  }, [subsQ.data, isSuperAdmin, subscriptionNow]);

  const productMeta = useMemo<Record<TildeProduct, ProductMeta>>(() => {
    const meta = { ...PRODUCT_META };
    const empresa = (subsQ.data ?? []).find(
      (s) => s.product === 'Empresa' && isUsable(s, subscriptionNow),
    );
    const commercialProduct = empresa
      ? commercialProductFromTechnical('Empresa', empresa.tier, empresa.commercialProduct)
      : 'Empresa';
    if (commercialProduct === 'Monotributo') {
      meta.Empresa = { ...meta.Empresa, label: 'Monotributo', caption: empresa?.tierName ?? 'Facturación simple' };
    } else if (commercialProduct === 'ResponsableInscripto') {
      meta.Empresa = { ...meta.Empresa, label: 'Responsable Inscripto', caption: empresa?.tierName ?? 'IVA RI' };
    } else if (commercialProduct === 'Empresa') {
      meta.Empresa = { ...meta.Empresa, label: 'Producto por clasificar', caption: 'Revisá Producto y pagos' };
    }
    return meta;
  }, [subsQ.data, subscriptionNow]);

  const commercialProducts = useMemo<Record<TildeProduct, CommercialProductCode | 'Admin'>>(() => {
    const empresa = (subsQ.data ?? []).find(
      (s) => s.product === 'Empresa' && isUsable(s, subscriptionNow),
    );
    return {
      Empresa: empresa ? commercialProductFromTechnical('Empresa', empresa.tier, empresa.commercialProduct) : 'Empresa',
      Estudio: 'EstudioContable',
      Api: 'ApiProgramadores',
      Admin: 'Admin',
    };
  }, [subsQ.data, subscriptionNow]);

  const [activeProduct, setActiveInner] = useState<TildeProduct>(DEFAULT_PRODUCT);

  const activeSubscription = useMemo(
    () => (subsQ.data ?? []).find(
      (subscription) => subscription.product === activeProduct && isUsable(subscription, subscriptionNow),
    ) ?? null,
    [activeProduct, subsQ.data, subscriptionNow],
  );

  const hasConfirmedProduct = useMemo(() => {
    if (activeProduct === 'Admin') return true;
    if (!activeSubscription) return false;
    if (activeSubscription.product !== 'Empresa') return true;
    return commercialProductFromTechnical(
      activeSubscription.product,
      activeSubscription.tier,
      activeSubscription.commercialProduct,
    ) !== 'Empresa';
  }, [activeProduct, activeSubscription]);

  // Inicializa / reconcilia el producto activo cuando cambian tenant o disponibilidad.
  useEffect(() => {
    if (!tenantId) return;
    const persisted = (() => {
      try {
        return localStorage.getItem(storageKey(tenantId)) as TildeProduct | null;
      } catch {
        return null;
      }
    })();
    setActiveInner((current) => {
      const candidate = persisted ?? current;
      return availableProducts.includes(candidate) ? candidate : availableProducts[0] ?? DEFAULT_PRODUCT;
    });
  }, [tenantId, availableProducts]);

  const setActiveProduct = useCallback(
    (product: TildeProduct) => {
      setActiveInner(product);
      if (tenantId) {
        try {
          localStorage.setItem(storageKey(tenantId), product);
        } catch {
          /* ignore quota/availability */
        }
      }
    },
    [tenantId],
  );

  const value = useMemo<ProductContextValue>(
    () => ({
      activeProduct,
      setActiveProduct,
      availableProducts,
      productMeta,
      activeCommercialProduct: commercialProducts[activeProduct],
      hasConfirmedProduct,
      activeTier: activeSubscription?.tier ?? null,
      activeFeatures: activeSubscription?.features ?? [],
      activeLimits: activeSubscription?.limits ?? {},
      isLoading: subsQ.isLoading,
    }),
    [
      activeProduct,
      setActiveProduct,
      availableProducts,
      productMeta,
      commercialProducts,
      hasConfirmedProduct,
      activeSubscription,
      subsQ.isLoading,
    ],
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProduct(): ProductContextValue {
  const ctx = useContext(ProductContext);
  if (!ctx) {
    throw new Error('useProduct debe usarse dentro de <ProductProvider>.');
  }
  return ctx;
}
